import {
  ActionIcon, Badge, Button, Card, Group, Modal, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Tabs, Text, TextInput,
  Title, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCalendarRepeat, IconCopy, IconEdit, IconPlayerPlay, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { downloadFile, http } from "../../api/http";
import { useCan, useUser } from "../../auth/user";
import { Chart } from "../../components/Charts";
import { DataTable } from "../../components/DataTable";
import { IssueFields, type IssueOptions, runReport, usePurpose } from "../../components/ReportButton";
import { fmtDateTime, KpiGrid, PageHeader, StatusBadge } from "../../components/ui";
import { useT } from "../../i18n/script";
import { BuilderPanel } from "./BuilderPanel";

export interface ParamDef { name: string; label: string; type: string; default?: unknown; required?: boolean }
export interface Template {
  id: number; code: string; name: string; description?: string; kind: "system" | "builder"; audience: string; category: string;
  params: ParamDef[]; roles: string[]; shared: boolean; active: boolean; created_by?: string; source?: string | null; definition?: Record<string, unknown>;
}
interface DocSection { kind: string; title?: string; rows: Record<string, unknown>[]; columns: { key: string; label: string; type: string; decimals?: number }[];
  totals?: Record<string, unknown> | null; items: { label: string; value: string | number; unit?: string }[]; chart?: { type: string; x: string; series: { key: string; label: string }[] };
  text?: string; note?: string }

export function ParamsForm({ defs, values, onChange }: { defs: ParamDef[]; values: Record<string, unknown>; onChange: (v: Record<string, unknown>) => void }) {
  const { t } = useT();
  const needs = (tp: string) => defs.some((d) => d.type === tp);
  const orchards = useQuery({ queryKey: ["orchards-sel"], enabled: needs("orchard"), queryFn: () => http.get<{ id: number; name: string; producer?: string }[]>("/api/v1/orchards") });
  const lots = useQuery({ queryKey: ["lots-sel", values.sezona], enabled: needs("lot"), queryFn: () => http.get<{ id: number; lot_code: string; orchard: string }[]>("/api/v1/diary/lots", { season: values.sezona as number }) });
  const producers = useQuery({ queryKey: ["producers-sel"], enabled: needs("producer"), queryFn: () => http.get<{ bpg: string; name: string }[]>("/api/v1/producers", { limit: 2000 }) });
  const set = (k: string, v: unknown) => onChange({ ...values, [k]: v });
  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }}>
      {defs.map((d) => {
        const common = { key: d.name, label: t(d.label), required: d.required };
        if (d.type === "integer") return <NumberInput {...common} value={(values[d.name] as number) ?? ""} onChange={(v) => set(d.name, v === "" ? null : Number(v))} />;
        if (d.type === "date") return <TextInput {...common} type="date" value={(values[d.name] as string) ?? ""} onChange={(e) => set(d.name, e.currentTarget.value || null)} />;
        if (d.type === "orchard") return <Select {...common} searchable data={(orchards.data ?? []).map((o) => ({ value: String(o.id), label: `${o.name}${o.producer ? ` — ${o.producer}` : ""}` }))}
          value={values[d.name] ? String(values[d.name]) : null} onChange={(v) => set(d.name, v ? Number(v) : null)} />;
        if (d.type === "lot") return <Select {...common} searchable data={(lots.data ?? []).map((o) => ({ value: String(o.id), label: `${o.lot_code} — ${o.orchard}` }))}
          value={values[d.name] ? String(values[d.name]) : null} onChange={(v) => set(d.name, v ? Number(v) : null)} />;
        if (d.type === "producer") return <Select {...common} searchable data={(producers.data ?? []).map((o) => ({ value: o.bpg, label: `${o.name} (${o.bpg})` }))}
          value={(values[d.name] as string) ?? null} onChange={(v) => set(d.name, v)} />;
        return <TextInput {...common} value={String(values[d.name] ?? "")} onChange={(e) => set(d.name, e.currentTarget.value)} />;
      })}
    </SimpleGrid>
  );
}

export function initialParams(defs: ParamDef[]): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  for (const d of defs) o[d.name] = d.default === "current_year" ? new Date().getFullYear() : d.default ?? null;
  return o;
}

function Preview({ sections }: { sections: DocSection[] }) {
  const { t } = useT();
  return (
    <Stack>
      {sections.map((s, i) => (
        <div key={i}>
          {s.title && <Title order={6} mb={4}>{s.title}</Title>}
          {s.kind === "kpi" && <KpiGrid cols={Math.min(4, s.items.length)} items={s.items.map((k) => ({ label: k.label, value: k.value, unit: k.unit }))} />}
          {s.kind === "text" && <Text size="sm">{s.text}</Text>}
          {s.kind === "chart" && s.chart && <Card withBorder><Chart rows={s.rows} x={s.chart.x} series={s.chart.series} type={s.chart.type === "line" ? "line" : "bar"} /></Card>}
          {s.kind === "table" && <DataTable rows={s.rows} totals={s.totals} maxHeight={420} exportName={s.title ?? "izvjestaj"}
            columns={s.columns.map((c) => ({ key: c.key, label: c.label, type: (c.type === "percent" ? "number" : c.type) as "text", decimals: c.decimals }))} />}
          {s.note && <Text size="xs" c="dimmed">{t(s.note)}</Text>}
        </div>
      ))}
    </Stack>
  );
}

function RunModal({ tpl, onClose }: { tpl: Template; onClose: () => void }) {
  const { t } = useT();
  const can = useCan();
  const [params, setParams] = useState<Record<string, unknown>>(initialParams(tpl.params));
  const [archive, setArchive] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ sections: DocSection[] } | null>(null);
  const { script } = useT();
  const [opts, setOpts] = useState<IssueOptions>({ script, number: "", purpose: null });
  const purpose = usePurpose(tpl.code, params);
  const go = async (fmt: string) => {
    setBusy(fmt);
    try {
      if (fmt === "json") setPreview(await http.post<{ sections: DocSection[] }>(`/api/v1/reports/templates/${tpl.code}/run`, { params, format: "json" }));
      else {
        const r = await runReport(tpl.code, params, fmt, archive, { ...opts, purpose: opts.purpose ?? purpose });
        if (archive) notifications.show({ color: "green", message: `${t("Arhivirano u DMS, dokument")} ${r.headers.get("X-Document-Id")}` });
      }
    } catch (e) { notifications.show({ color: "red", message: (e as Error).message }); } finally { setBusy(null); }
  };
  return (
    <Modal opened onClose={onClose} size={preview ? "95%" : "lg"} title={<Text fw={650}>{tpl.name}</Text>}>
      <Stack>
        {tpl.description && <Text size="sm" c="dimmed">{tpl.description}</Text>}
        {tpl.params.length > 0 && <ParamsForm defs={tpl.params} values={params} onChange={setParams} />}
        <IssueFields archive={archive} setArchive={can("dms:write") ? setArchive : undefined}
          value={{ ...opts, purpose: opts.purpose ?? purpose }} onChange={setOpts} />
        <Group justify="flex-end">
          <Group gap="xs">
            <Button variant="default" loading={busy === "json"} onClick={() => go("json")}>{t("Pregled")}</Button>
            <Button loading={busy === "pdf"} onClick={() => go("pdf")}>PDF</Button>
            <Button variant="light" loading={busy === "xlsx"} onClick={() => go("xlsx")}>Excel</Button>
            <Button variant="light" loading={busy === "csv"} onClick={() => go("csv")}>CSV</Button>
          </Group>
        </Group>
        {preview && <Preview sections={preview.sections} />}
      </Stack>
    </Modal>
  );
}

function ScheduleModal({ templates, onClose }: { templates: Template[]; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [code, setCode] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [period, setPeriod] = useState("mjesecno");
  const [fmt, setFmt] = useState("pdf");
  const tpl = templates.find((x) => x.code === code);
  const fixed = (tpl?.params ?? []).filter((p) => !["od", "do", "sezona", "godina"].includes(p.name));
  const [params, setParams] = useState<Record<string, unknown>>({});
  const m = useMutation({
    mutationFn: () => http.post("/api/v1/reports/schedules", { template_code: code, name: name || tpl?.name, period, params, output_format: fmt }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["schedules"] }); onClose(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} title={t("Novi periodični izvještaj")} size="lg">
      <Stack>
        <Select label={t("Izvještaj")} searchable data={templates.map((x) => ({ value: x.code, label: `${x.name} (${x.category})` }))} value={code} onChange={setCode} />
        <TextInput label={t("Naziv rasporeda")} value={name} onChange={(e) => setName(e.currentTarget.value)} placeholder={tpl?.name} />
        <SegmentedControl value={period} onChange={setPeriod} data={[{ value: "mjesecno", label: t("Mjesečno") }, { value: "kvartalno", label: t("Kvartalno") },
          { value: "godisnje", label: t("Godišnje") }]} />
        <SegmentedControl value={fmt} onChange={setFmt} data={["pdf", "xlsx", "csv"]} />
        {fixed.length > 0 && <ParamsForm defs={fixed} values={params} onChange={setParams} />}
        <Text size="xs" c="dimmed">{t("Izvršava se prvog dana perioda za prethodni period (od/do/sezona se računaju automatski), sa vašim opsegom podataka; rezultat se arhivira u DMS.")}</Text>
        <Group justify="flex-end"><Button onClick={() => m.mutate()} disabled={!code} loading={m.isPending}>{t("Sačuvaj raspored")}</Button></Group>
      </Stack>
    </Modal>
  );
}

export default function ReportsPage() {
  const { t } = useT();
  const can = useCan();
  const user = useUser();
  const qc = useQueryClient();
  const [run, setRun] = useState<Template | null>(null);
  const [edit, setEdit] = useState<Template | "new" | null>(null);
  const [sched, setSched] = useState(false);
  const [tab, setTab] = useState<string | null>("katalog");
  const [aud, setAud] = useState("sve");
  const tpls = useQuery({ queryKey: ["report-templates"], queryFn: () => http.get<Template[]>("/api/v1/reports/templates") });
  const runs = useQuery({ queryKey: ["report-runs"], enabled: tab === "istorija", queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/reports/runs", { mine: !can("reports:manage") }) });
  const schedules = useQuery({ queryKey: ["schedules"], enabled: can("reports:schedule"), queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/reports/schedules") });
  const copy = useMutation({ mutationFn: (code: string) => http.post<Template>(`/api/v1/reports/templates/${code}/copy`),
    onSuccess: (tp) => { qc.invalidateQueries({ queryKey: ["report-templates"] }); setEdit(tp); setTab("graditelj"); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }) });
  const del = useMutation({ mutationFn: (code: string) => http.del(`/api/v1/reports/templates/${code}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["report-templates"] }), onError: (e: Error) => notifications.show({ color: "red", message: e.message }) });
  const delS = useMutation({ mutationFn: (id: number) => http.del(`/api/v1/reports/schedules/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules"] }) });
  const runS = useMutation({ mutationFn: (id: number) => http.post<{ reg_no: string }>(`/api/v1/reports/schedules/${id}/run`),
    onSuccess: (r) => notifications.show({ color: "green", message: `${t("Izvršeno, arhivirano pod brojem")} ${r.reg_no}` }),
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }) });

  const all = (tpls.data ?? []).filter((x) => aud === "sve" || x.audience === aud || x.audience === "oba");
  const cats = Array.from(new Set(all.map((x) => x.category)));
  return (
    <Stack>
      <PageHeader title="Izvještaji" description="Sistemski izvještaji za proizvođača i Agenciju, periodični izvještaji i graditelj sopstvenih izvještaja. Izbor opštine u zaglavlju filtrira podatke svih izvještaja." />
      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="katalog">{t("Katalog")}</Tabs.Tab>
          {can("reports:builder") && <Tabs.Tab value="graditelj">{t("Graditelj izvještaja")}</Tabs.Tab>}
          {can("reports:schedule") && <Tabs.Tab value="rasporedi">{t("Periodični izvještaji")}</Tabs.Tab>}
          <Tabs.Tab value="istorija">{t("Istorija")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="katalog" pt="md">
          <SegmentedControl mb="md" value={aud} onChange={setAud} data={[{ value: "sve", label: t("Svi") }, { value: "proizvodjac", label: t("Za proizvođača") },
            { value: "agencija", label: t("Za Agenciju / opštinu") }]} />
          {cats.map((c) => (
            <div key={c}>
              <Title order={5} mt="md" mb="xs">{t(c)}</Title>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                {all.filter((x) => x.category === c).map((x) => (
                  <Card key={x.code} withBorder padding="md">
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Text fw={600}>{x.name}</Text>
                      <Group gap={4} wrap="nowrap">
                        {x.kind === "builder" && <Badge size="xs" color="soil" variant="light">{x.shared ? t("dijeljen") : t("lični")}</Badge>}
                        <Badge size="xs" variant="outline">{x.audience}</Badge>
                      </Group>
                    </Group>
                    <Text size="sm" c="dimmed" mt={4} lineClamp={3}>{x.description}</Text>
                    <Group mt="sm" gap="xs">
                      <Button size="xs" leftSection={<IconPlayerPlay size={14} />} onClick={() => setRun(x)}>{t("Pokreni")}</Button>
                      {can("reports:builder") && x.source && <Tooltip label={t("Kopiraj u lični izvještaj i prilagodi")}>
                        <ActionIcon variant="light" onClick={() => copy.mutate(x.code)}><IconCopy size={16} /></ActionIcon></Tooltip>}
                      {x.kind === "builder" && (x.created_by === user.username || can("reports:manage")) && <>
                        <ActionIcon variant="light" onClick={() => { setEdit(x); setTab("graditelj"); }}><IconEdit size={16} /></ActionIcon>
                        <ActionIcon variant="light" color="red" onClick={() => del.mutate(x.code)}><IconTrash size={16} /></ActionIcon></>}
                    </Group>
                  </Card>
                ))}
              </SimpleGrid>
            </div>
          ))}
        </Tabs.Panel>
        <Tabs.Panel value="graditelj" pt="md">
          {tab === "graditelj" && <BuilderPanel key={edit === "new" || !edit ? "new" : edit.code} template={edit === "new" ? null : edit}
            onSaved={() => { qc.invalidateQueries({ queryKey: ["report-templates"] }); setEdit(null); setTab("katalog"); }} />}
        </Tabs.Panel>
        <Tabs.Panel value="rasporedi" pt="md">
          <Group justify="flex-end" mb="sm"><Button leftSection={<IconCalendarRepeat size={16} />} onClick={() => setSched(true)}>{t("Novi raspored")}</Button></Group>
          <DataTable rows={schedules.data ?? []} columns={[
            { key: "name", label: "Naziv" }, { key: "template_name", label: "Izvještaj" }, { key: "period", label: "Period" },
            { key: "output_format", label: "Format" }, { key: "created_by", label: "Autor" },
            { key: "last_run_at", label: "Posljednje izvršenje", render: (r) => fmtDateTime(r.last_run_at) },
            { key: "a", label: "", render: (r) => <Group gap={4}>
              <Tooltip label={t("Izvrši sada (za prethodni period)")}><ActionIcon variant="light" onClick={() => runS.mutate(Number(r.id))}><IconPlayerPlay size={14} /></ActionIcon></Tooltip>
              <ActionIcon variant="light" color="red" onClick={() => delS.mutate(Number(r.id))}><IconTrash size={14} /></ActionIcon></Group> },
          ]} />
        </Tabs.Panel>
        <Tabs.Panel value="istorija" pt="md">
          <DataTable rows={runs.data ?? []} columns={[
            { key: "created_at", label: "Vrijeme", render: (r) => fmtDateTime(r.created_at) }, { key: "name", label: "Izvještaj" },
            { key: "params", label: "Parametri", render: (r) => <Text size="xs">{Object.entries((r.params as Record<string, unknown>) ?? {}).map(([k, v]) => `${k}=${String(v)}`).join(", ")}</Text> },
            { key: "output_format", label: "Format" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
            { key: "reg_no", label: "DMS broj", render: (r) => r.document_id ? <Text size="xs" c="blue" style={{ cursor: "pointer" }}
              onClick={() => downloadFile(`/api/v1/dms/documents/${r.document_id}/versions/1/file`, {}, true)}>{String(r.reg_no)}</Text> : "—" },
            { key: "created_by", label: "Korisnik" }, { key: "error", label: "Greška" },
          ]} />
        </Tabs.Panel>
      </Tabs>
      {run && <RunModal tpl={run} onClose={() => setRun(null)} />}
      {sched && <ScheduleModal templates={tpls.data ?? []} onClose={() => setSched(false)} />}
    </Stack>
  );
}
